"use client";

import { useState, useEffect } from "react";
import { User, Users, DollarSign, FileText } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/app/_module/components/ui/accordion";

import { GeneralSection } from "./GeneralSection";
import { FamilySection } from "./FamilySection";
import { IncomeSection } from "./IncomeSection";
import { CustomFieldsSection } from "./CustomFieldsSection";

const STORAGE_KEY = "lead-detail-sections";

interface SectionVisibility {
  family: boolean;
  income: boolean;
  customFields: boolean;
}

const DEFAULT_VISIBILITY: SectionVisibility = {
  family: true,
  income: true,
  customFields: true,
};

interface UnifiedGeneralCardProps {
  isEditing: boolean;
  leadData?: any;
  leadId?: string;
  onCallNumber?: (phone: string) => void;
}

export const UnifiedGeneralCard = function ({
  isEditing,
  leadData,
  leadId,
  onCallNumber,
}: UnifiedGeneralCardProps) {
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(DEFAULT_VISIBILITY);

  // Load visibility from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          setSectionVisibility({ ...DEFAULT_VISIBILITY, ...JSON.parse(stored) });
        }
      } catch {
        // ignore
      }
    }
  }, []);

  // Persist visibility changes
  const updateVisibility = (key: keyof SectionVisibility, value: boolean) => {
    setSectionVisibility((prev) => {
      const next = { ...prev, [key]: value };
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  };

  // Only custom fields starts expanded; family & income start collapsed
  const defaultOpenSections = ["customFields"];

  return (
    <Card className="shadow-sm border-gray-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <User className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-lg font-semibold text-gray-900">
            General Information
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="p-6 pt-0">
        {/* General fields - always visible */}
        <GeneralSection isEditing={isEditing} leadData={leadData} bare />

        {/* Collapsible subsections */}
        <Accordion
          type="multiple"
          defaultValue={defaultOpenSections}
          className="mt-6"
        >
          {/* More Information (Custom Fields) */}
          {sectionVisibility.customFields && (
            <AccordionItem value="customFields">
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-gray-900">More Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CustomFieldsSection isEditing={isEditing} leadId={leadId} bare onCallNumber={onCallNumber} />
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Family Information */}
          {sectionVisibility.family && (
            <AccordionItem value="family">
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-gray-900">Family Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <FamilySection isEditing={isEditing} leadData={leadData} bare />
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Income Information */}
          {sectionVisibility.income && (
            <AccordionItem value="income">
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-gray-900">Income Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <IncomeSection isEditing={isEditing} bare />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </CardContent>
    </Card>
  );
};
